import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Quote } from "lucide-react";
import type { Workspace } from "@/hooks/useWorkspace";
import type { ReviewExcerpt } from "./useDowngradeReviewData";

const labelFor = (w: { workspace_label: string | null; display_name: string | null }) =>
  w.workspace_label || w.display_name || "Workspace";

const truncate = (s: string, n = 140) => (s.length > n ? `${s.slice(0, n).trim()}…` : s);

type Props = {
  loading: boolean;
  excerpts: ReviewExcerpt[];
  workspaces: Workspace[];
  selectedExcerpts: Set<string>;
  busy: boolean;
  cutoverDate: string;
  onToggleExcerpt: (id: string) => void;
  onToggleAll: () => void;
  onBulkDelete: () => void;
  onDeleteOne: (id: string) => void;
};

export const AtRiskExcerptsCard = ({
  loading,
  excerpts,
  workspaces,
  selectedExcerpts,
  busy,
  cutoverDate,
  onToggleExcerpt,
  onToggleAll,
  onBulkDelete,
  onDeleteOne,
}: Props) => {
  const allExcerptsSelected = excerpts.length > 0 && selectedExcerpts.size === excerpts.length;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="text-base">At-risk excerpts ({excerpts.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : excerpts.length === 0 ? (
          <div className="text-center py-6 space-y-2">
            <Quote className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No excerpts in the affected workspaces.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              Excerpts are bound to a single workspace and don't follow library items when you move them. Anything left here will be archived on {cutoverDate}. To keep an excerpt, move its parent library item to a surviving workspace and re-create the excerpt there, or delete it now.
            </p>

            <div className="flex flex-wrap items-center gap-2 border-y border-border py-2">
              <button
                type="button"
                onClick={onToggleAll}
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Checkbox checked={allExcerptsSelected} />
                <span>{selectedExcerpts.size} selected</span>
              </button>
              <div className="ml-auto">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={onBulkDelete}
                  disabled={busy || selectedExcerpts.size === 0}
                >
                  Delete now
                </Button>
              </div>
            </div>

            <ul className="divide-y divide-border rounded-md border border-border">
              {excerpts.map((ex) => {
                const owning = workspaces.find((w) => w.id === ex.workspace_id);
                return (
                  <li key={ex.id} className="flex flex-wrap items-start justify-between gap-3 px-3 py-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <Checkbox
                        checked={selectedExcerpts.has(ex.id)}
                        onCheckedChange={() => onToggleExcerpt(ex.id)}
                        className="mt-1"
                      />
                      <div className="min-w-0 space-y-1">
                        <p className="text-sm text-foreground">
                          “{truncate(ex.body_text)}”
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {ex.libraryItemTitle ? `From ${ex.libraryItemTitle} · ` : ""}
                          in <span className="text-foreground">{owning ? labelFor(owning) : "—"}</span>
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDeleteOne(ex.id)}
                      disabled={busy}
                    >
                      Delete
                    </Button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
};
